import React from "react";

/**
 * Anivaze v2 — Reusable ACTORS.
 *
 * Independent, self-animating characters/props. Each is a pure function of
 * (position, scale, t) returning SVG, so the SAME actor can be dropped into ANY
 * scene and it runs its own animation (a butterfly flaps and bobs whether it's
 * in the life-cycle lesson or leading a child down a road). This is the
 * composable layer beneath "scenes" — scenes choreograph actors along paths.
 *
 * Convention: actor(x, y, t, scale?, ...opts) → JSX. Animate purely from `t`.
 */

export function Butterfly(x: number, y: number, t: number, scale = 1, color = "#f472b6"): React.ReactNode {
  const flap = Math.abs(Math.sin(t * 8));
  const wx = 6 + flap * 8;
  const by = Math.sin(t * 3) * 3;
  return (
    <g transform={`translate(${x} ${y + by}) scale(${scale})`}>
      <ellipse cx={-9} cy={-4} rx={wx} ry={11} fill={color} opacity={0.92} transform={`rotate(${-flap * 12} -2 0)`} />
      <ellipse cx={9} cy={-4} rx={wx} ry={11} fill={color} opacity={0.92} transform={`rotate(${flap * 12} 2 0)`} />
      <ellipse cx={-8} cy={9} rx={wx * 0.7} ry={8} fill={color} opacity={0.7} />
      <ellipse cx={8} cy={9} rx={wx * 0.7} ry={8} fill={color} opacity={0.7} />
      <ellipse cx={0} cy={0} rx={2.4} ry={12} fill="#4c1d95" />
      <path d="M0,-10 Q-4,-18 -7,-19" fill="none" stroke="#4c1d95" strokeWidth={1.2} />
      <path d="M0,-10 Q4,-18 7,-19" fill="none" stroke="#4c1d95" strokeWidth={1.2} />
      <circle cx={-7} cy={-19} r={1.6} fill="#4c1d95" />
      <circle cx={7} cy={-19} r={1.6} fill="#4c1d95" />
    </g>
  );
}

export function Worm(x: number, y: number, t: number, scale = 1, color = "#84cc16"): React.ReactNode {
  const head = -Math.max(0, Math.sin(t * 5)) * 4;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      {Array.from({ length: 6 }, (_, i) => {
        const ph = t * 5 - i * 0.7;
        const sx = i * 9 - Math.max(0, Math.sin(ph)) * 4;
        const sy = -Math.abs(Math.sin(ph)) * 4;
        return <circle key={i} cx={sx} cy={sy} r={6 - i * 0.35} fill={color} opacity={0.95} />;
      })}
      <circle cx={-1} cy={head - 1.5} r={1.5} fill="#1f2937" />
    </g>
  );
}

export function WalkingChild(x: number, y: number, t: number, scale = 1, flip = false): React.ReactNode {
  const step = Math.sin(t * 6);
  const bob = Math.abs(Math.sin(t * 6)) * 2;
  const dir = flip ? -1 : 1;
  return (
    <g transform={`translate(${x} ${y - bob}) scale(${scale * dir} ${scale})`}>
      <line x1={-3} y1={0} x2={-3 + step * 6} y2={22} stroke="#1e3a8a" strokeWidth={5} strokeLinecap="round" />
      <line x1={3} y1={0} x2={3 - step * 6} y2={22} stroke="#1d4ed8" strokeWidth={5} strokeLinecap="round" />
      <rect x={-9} y={-22} width={18} height={26} rx={8} fill="#f59e0b" />
      <line x1={-7} y1={-16} x2={-7 - step * 5} y2={-2} stroke="#fbbf24" strokeWidth={4} strokeLinecap="round" />
      <line x1={7} y1={-16} x2={7 + step * 5} y2={-2} stroke="#fbbf24" strokeWidth={4} strokeLinecap="round" />
      <circle cx={0} cy={-34} r={13} fill="#fcd34d" />
      <path d="M-13,-38 Q0,-52 13,-38 Q8,-46 0,-46 Q-8,-46 -13,-38" fill="#92400e" />
      <circle cx={5} cy={-35} r={1.8} fill="#1f2937" />
      <path d="M2,-30 Q6,-27 10,-30" fill="none" stroke="#7f1d1d" strokeWidth={1.5} strokeLinecap="round" />
    </g>
  );
}

export function Flower(x: number, y: number, t: number, scale = 1, color = "#f472b6"): React.ReactNode {
  const sway = Math.sin(t * 1.5 + x * 0.05) * 4;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <path d={`M0,0 Q${sway},-15 ${sway},-30`} fill="none" stroke="#16a34a" strokeWidth={3} />
      <ellipse cx={sway - 6} cy={-16} rx={6} ry={3} fill="#22c55e" transform={`rotate(-30 ${sway - 6} -16)`} />
      <g transform={`translate(${sway} -32)`}>
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * Math.PI * 2;
          return <ellipse key={i} cx={Math.cos(a) * 8} cy={Math.sin(a) * 8} rx={5} ry={7} fill={color} transform={`rotate(${(a * 180) / Math.PI} ${Math.cos(a) * 8} ${Math.sin(a) * 8})`} />;
        })}
        <circle r={5} fill="#fde047" />
      </g>
    </g>
  );
}

export function TreeActor(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const sway = Math.sin(t * 1.2 + x * 0.1) * 3;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <rect x={-5} y={-40} width={10} height={40} fill="#92400e" />
      <g transform={`translate(${sway} 0)`}>
        <circle cx={0} cy={-52} r={26} fill="#16a34a" />
        <circle cx={-18} cy={-42} r={18} fill="#22c55e" />
        <circle cx={18} cy={-42} r={18} fill="#22c55e" />
      </g>
    </g>
  );
}

export function SunActor(x: number, y: number, t: number, scale = 1): React.ReactNode {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <g transform={`rotate(${(t * 10) % 360})`}>
        {Array.from({ length: 12 }, (_, i) => {
          const a = (i / 12) * Math.PI * 2;
          return <line key={i} x1={Math.cos(a) * 30} y1={Math.sin(a) * 30} x2={Math.cos(a) * 42} y2={Math.sin(a) * 42} stroke="#fcd34d" strokeWidth={3} strokeLinecap="round" />;
        })}
      </g>
      <circle r={24} fill="#fbbf24" />
      <circle cx={-7} cy={-3} r={2.5} fill="#92400e" />
      <circle cx={7} cy={-3} r={2.5} fill="#92400e" />
      <path d="M-7,5 Q0,11 7,5" fill="none" stroke="#92400e" strokeWidth={2} strokeLinecap="round" />
    </g>
  );
}

export function Bird(x: number, y: number, t: number, scale = 1): React.ReactNode {
  const flap = Math.sin(t * 7 + x) * 8;
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`}>
      <path d={`M-12,0 Q-4,${-flap} 0,0 Q4,${-flap} 12,0`} fill="none" stroke="#475569" strokeWidth={2.5} strokeLinecap="round" />
    </g>
  );
}

/**
 * Xiaohei — a minimalist hand-drawn character (MIT-licensed style from
 * helloianneo/ian-xiaohei-illustrations): black figure, white dot eyes, thin
 * limbs, blank face. Designed to ACT (point, walk, think, cheer), not decorate.
 * This is our first "illustration-style actor" — a different visual LANGUAGE
 * than a filter. Pure function of t.
 */
export function Xiaohei(
  x: number, y: number, t: number, scale = 1,
  pose: "idle" | "point" | "think" | "cheer" | "walk" = "idle",
): React.ReactNode {
  const bob = Math.sin(t * 2.2) * 1.5;
  const step = Math.sin(t * 6);
  const ink = "#1a1a1a";
  const lArm = pose === "cheer" ? "M-6,-30 L-15,-47" : "M-6,-30 L-12,-15";
  const rArm = pose === "cheer" ? "M6,-30 L15,-47"
    : pose === "point" ? "M6,-30 L22,-52"
    : pose === "think" ? "M6,-30 L2,-43" : "M6,-30 L12,-15";
  const legL = pose === "walk" ? `M-2,-8 L${-5 + step * 5},2` : "M-2,-8 L-6,2";
  const legR = pose === "walk" ? `M2,-8 L${5 - step * 5},2` : "M2,-8 L6,2";
  const tilt = pose === "think" ? -8 : 0;
  return (
    <g transform={`translate(${x} ${y + bob}) scale(${scale})`} stroke={ink} strokeWidth={2.4} strokeLinecap="round" fill="none">
      <path d="M0,-30 L0,-8" />
      <path d={lArm} />
      <path d={rArm} />
      <path d={legL} />
      <path d={legR} />
      <g transform={`rotate(${tilt} 0 -40)`}>
        <circle cx={0} cy={-40} r={12} fill={ink} stroke="none" />
        <circle cx={-4} cy={-42} r={2} fill="#fff" stroke="none" />
        <circle cx={4} cy={-42} r={2} fill="#fff" stroke="none" />
      </g>
    </g>
  );
}

export function Cloud(x: number, y: number, scale = 1): React.ReactNode {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={0.95}>
      <ellipse cx={0} cy={0} rx={28} ry={18} fill="#fff" />
      <ellipse cx={26} cy={5} rx={22} ry={14} fill="#fff" />
      <ellipse cx={-24} cy={6} rx={18} ry={12} fill="#fff" />
    </g>
  );
}
